import { createFileRoute } from "@tanstack/react-router";
import { useDossier } from "@/features/funnel/api/use-mission-file";

export const Route = createFileRoute("/dossier/$token")({
  component: DossierPage,
});

/**
 * An operative's dossier, for the people they granted clearance: progress
 * only — their name, the locks open on their vault, and each mission filed.
 */
function DossierPage() {
  const { token } = Route.useParams();
  const { data, isLoading, isError } = useDossier(token);
  const name = data?.name || "An operative";

  return (
    <section className="min-h-[100dvh] bg-[#0a0809] px-5 py-10 font-serif text-[#eadfc6]">
      <div className="mx-auto max-w-[440px]">
        <div className="text-center font-stencil text-[11px] tracking-[4px] text-[#d6a94e] uppercase [text-shadow:0_0_14px_rgba(214,169,78,.5)]">
          ▸ Northstar Agency · Clearance granted ◂
        </div>
        {isLoading ? (
          <p className="mt-10 text-center font-mono text-[12px] tracking-[.2em] uppercase opacity-70">
            Opening the dossier…
          </p>
        ) : isError || !data ? (
          <p className="mt-10 text-center text-[17px]">
            This dossier is sealed. Ask the operative for a fresh link.
          </p>
        ) : (
          <div className="mt-6 rounded-md bg-[#f3ecd8] px-5 py-6 text-[#2b2107] shadow-[0_20px_60px_rgba(0,0,0,.55)]">
            <div className="font-mono text-[11px] tracking-[.22em] text-[#8a7350] uppercase">
              Operative dossier
            </div>
            <div className="mt-1 font-stencil text-[30px] leading-[1.05] tracking-[.03em] text-[#a8271c] uppercase">
              {name}
            </div>

            <div className="mt-4 font-mono text-[11px] tracking-[.18em] text-[#5b4a2a] uppercase">
              Vault · {Math.min(data.completed, data.locks)} of {data.locks}{" "}
              locks open
            </div>
            <div className="mt-2 flex gap-1.5">
              {Array.from({ length: data.locks }, (_, i) => (
                <div
                  key={i}
                  className={`h-3 flex-1 rounded-sm ${
                    i < data.completed
                      ? "bg-[#d6a94e] shadow-[0_0_8px_rgba(214,169,78,.7)]"
                      : "bg-[#2b2107]/15"
                  }`}
                />
              ))}
            </div>

            <div className="mt-6 font-mono text-[11px] tracking-[.18em] text-[#5b4a2a] uppercase">
              Missions completed · {data.completed}
            </div>
            {data.missions.length ? (
              <ul className="mt-2 divide-y divide-[#2b2107]/15">
                {data.missions.map((m, i) => (
                  <li
                    key={i}
                    className="flex items-baseline justify-between gap-3 py-2"
                  >
                    <span className="font-stencil text-[15px] tracking-[.03em] uppercase">
                      {m.name}
                    </span>
                    {m.filedAt ? (
                      <span className="shrink-0 font-mono text-[11px] text-[#8a7350]">
                        {new Date(m.filedAt).toLocaleDateString()}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-[15px] italic opacity-70">
                First mission underway.
              </p>
            )}
          </div>
        )}
        <p className="mt-6 text-center text-[13px] italic opacity-60">
          You were cleared to watch this operative's progress.
        </p>
      </div>
    </section>
  );
}
